import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Upload, CheckCircle, XCircle } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface ImportResult {
  success: boolean;
  isbn: string;
  book_id?: string;
  book_title?: string;
  error?: string;
  action: 'linked' | 'created_and_linked' | 'failed';
}

interface BulkImportAwardBooksProps {
  awards: Array<{ id: string; prize_name: string; year: number }>;
}

export function BulkImportAwardBooks({ awards }: BulkImportAwardBooksProps) {
  const [selectedAwardId, setSelectedAwardId] = useState<string>('');
  const [isbnInput, setIsbnInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<ImportResult[]>([]);
  const { toast } = useToast();

  const selectedAward = awards.find(a => a.id === selectedAwardId);

  const handleImport = async () => {
    if (!selectedAwardId) {
      toast({
        title: 'No award selected',
        description: 'Please select an award first',
        variant: 'destructive'
      });
      return;
    }

    if (!isbnInput.trim()) {
      toast({
        title: 'No ISBNs provided',
        description: 'Please enter at least one ISBN',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    setResults([]);

    try {
      // Parse ISBNs from input (one per line or comma-separated)
      const isbns = isbnInput
        .split(/[\n,]/)
        .map(isbn => isbn.trim())
        .filter(isbn => isbn.length > 0);

      const books = isbns.map((isbn, index) => ({
        isbn,
        position: index + 1 // Optional: set position based on order
      }));

      const { data, error } = await supabase.functions.invoke('bulk-import-award-books', {
        body: {
          award_id: selectedAwardId,
          year: selectedAward?.year,
          books
        }
      });

      if (error) {
        throw new Error(error.message || 'Failed to invoke function');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      setResults(data.results || []);

      const successCount = data.successful || 0;
      const totalCount = data.total || 0;

      toast({
        title: 'Import complete',
        description: `Successfully imported ${successCount} of ${totalCount} books`,
        variant: successCount === totalCount ? 'default' : 'destructive'
      });

    } catch (error: any) {
      console.error('Import error:', error);
      const errorMessage = error?.message || 
                          (typeof error === 'object' && 'error' in error ? error.error : null) ||
                          "An unexpected error occurred";
      toast({
        title: "Import failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="p-6 space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Bulk Import Books to Award Shortlist</h3>
        <p className="text-sm text-muted-foreground">
          Enter ISBNs (one per line or comma-separated) to automatically search for books and add them to the shortlist
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="award-select">Select Award</Label>
          <Select value={selectedAwardId} onValueChange={setSelectedAwardId}>
            <SelectTrigger id="award-select" className="w-full">
              <SelectValue placeholder="Choose an award..." />
            </SelectTrigger>
            <SelectContent>
              {awards.map(award => (
                <SelectItem key={award.id} value={award.id}>
                  {award.prize_name} {award.year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="isbn-input">ISBNs (one per line or comma-separated)</Label>
          <Textarea
            id="isbn-input"
            placeholder="9781787334342&#10;9780861546459&#10;9781908745903"
            value={isbnInput}
            onChange={(e) => setIsbnInput(e.target.value)}
            rows={8}
            className="font-mono text-sm"
            disabled={isLoading}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Example: 9781787334342 or one ISBN per line
          </p>
        </div>

        <Button 
          onClick={handleImport} 
          disabled={isLoading || !selectedAwardId || !isbnInput.trim()}
          className="w-full"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Importing...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Import Books
            </>
          )}
        </Button>
      </div>

      {results.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-medium">Import Results</h4>
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {results.map((result, index) => (
              <div 
                key={index}
                className={`p-3 rounded-lg border text-sm ${
                  result.success 
                    ? 'bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800' 
                    : 'bg-red-50 border-red-200 dark:bg-red-950 dark:border-red-800'
                }`}
              >
                <div className="flex items-start gap-2">
                  {result.success ? (
                    <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">
                      {result.book_title || result.isbn}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      ISBN: {result.isbn}
                      {result.success && (
                        <span className="ml-2">
                          • {result.action === 'created_and_linked' ? 'Created & Linked' : 'Linked'}
                        </span>
                      )}
                      {result.error && (
                        <span className="ml-2 text-red-600 dark:text-red-400">
                          • {result.error}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
